import React, { useEffect, useRef, useState, useMemo } from 'react';
import type { Root as Am5Root } from '@amcharts/amcharts5';
import { createTimeSeriesChart, ChartConfig, yAxisInfoArray } from '../../utilities/chartUtils';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import EmptyState from '../empty-state/EmptyState';
import Loader from '../../features/common/Loader/Loader';
import FullScreenIcon from '../../features/common/FullScreenIcon/FullScreenIcon';
import { ReactComponent as CloseIcon } from '../../images/close-x-icon.svg';
import DownloadChart from './download/downloadChart';
import { handleChartDownloadUtil } from '../../utilities/ExportChart';
import { XYChart } from '@amcharts/amcharts5/xy';
import { syncCursor as syncCursorUtil, syncAxes as syncAxesUtil } from '../../utilities/chartHelpers';
import Helpers from '../../utilities/Helpers';
import { ProtectAction } from '../../rbac';
import NoDataComponent from '../../features/common/NoDataComponent/NoDataComponent';
import { calculateAndSetChartDataCounts } from '../../well-chart/WidgetLayoutSlice';
import { setChartsZoomState } from '../slices/AssetTrendDataSlice';

interface TimeSeriesDataProps {
  timeseriesChartData: any;
  yAxisRefreshVersion?: number;
  rPermission: { [key: string]: boolean };
  externalChartRefs?: React.MutableRefObject<(XYChart | null)[]>;
  onChartRegistered?: (index: number, chart: any) => void;
}
const TimeSeriesCharts: React.FC<TimeSeriesDataProps> = ({
  timeseriesChartData,
  yAxisRefreshVersion,
  rPermission,
  externalChartRefs,
  onChartRegistered,
}) => {
  const dispatch = useAppDispatch();
  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
  const amCharRefs = useRef<XYChart[] | null[]>([null, null, null, null]);
  const fullscreenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fullscreenChartRootRef = useRef<Am5Root | null>(null);

  const chartRootsRef = useRef<(Am5Root | null)[]>([]);
  const [fullscreenChartIndex, setFullscreenChartIndex] = useState<number | null>(null);
  // Cache the non-fullscreen chart instance and its zoom when entering fullscreen
  const preFullscreenChartRef = useRef<XYChart | null>(null);
  const preFullscreenZoomRef = useRef<{
    index: number;
    start?: number;
    end?: number;
  } | null>(null);
  const chartData = useAppSelector((state: any) => state.AssetTrendDataSlice.data);
  const chartDataLoading = useAppSelector((state: any) => state.AssetTrendDataSlice.loading);
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);

  type TrendConfig = {
    name: string;
    lineColor?: string;
    lineType?: string;
    lineWeight?: number;
    markerColor?: string;
    markerType?: string;
    markerSize?: number;
    selected?: boolean;
    displayOrder?: number | string;
  };
  const parameterData =
    (timeseriesChartData?.widgetProperty?.widgetProperties?.parameterConfig?.[0]?.parameters as
      | TrendConfig[]
      | undefined) ?? [];
  const rawDownTimeSliceData = useAppSelector((state: any) => state.shutdownAndDropdownSlice?.data?.downtimeData);
  const downTimeSliceData = useMemo(() => rawDownTimeSliceData ?? [], [rawDownTimeSliceData]);
  const pumpCheckerEventsSlice = useAppSelector((state: any) => state.PumpCheckerEventsSlice.data);

  // Memoize sorted chart data
  type ChartItem = {
    id: string | number;
    title: string;
    originalTitle?: string;
    dataPoints?: any[];
    maxThresholdValues?: any[];
    minThresholdValues?: any[];
    shortUnitOfMeasure?: string;
    minValue?: number;
    medianValue?: number;
    maxValue?: number;
    value?: number | string;
    date?: string;
    displayOrder?: number;
    selected?: boolean;
  };

  const sortedData = useMemo<ChartItem[]>(() => {
    if (!chartData || chartData.length === 0) return [];

    // For rl-well-charts, preserve parameter order from widget API
    if (chartType === 'rl-well-charts' && parameterData) {
      return parameterData
        .map((param: any) => {
          if (param?.selected === false) return null;

          const chart = chartData?.find((c: any) => String(c.address) === String(param.address));

          return {
            ...(chart || {}),
            id: param.address,
            originalTitle: param.name,
            title: Helpers.toSentenceCase(param.name),
            displayOrder: Number(param?.displayOrder) || 0,
            selected: param?.selected ?? true,
          };
        })
        .filter((item): item is ChartItem => item !== null);
    }

    return chartData
      .flatMap((chart: any) => {
        const param = parameterData?.find((p) => p.name.trim().toLowerCase() === chart.title.trim().toLowerCase());

        // Exclude chart if param.selected is explicitly false
        if (param?.selected === false) return [];

        return {
          ...chart,
          originalTitle: chart.title, // Keep original for parameter matching
          title: Helpers.toSentenceCase(chart.title), // Display sentence-cased version
          displayOrder: Number(param?.displayOrder) || 0,
          selected: param?.selected ?? true,
        };
      })
      .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) as ChartItem[];
  }, [chartData, parameterData, chartType]);

  const hasPlottableData = sortedData.some((d) => Array.isArray(d.dataPoints) && d.dataPoints.length > 0);

  // Cleanup: Dispose all resources when component unmounts
  useEffect(() => {
    return () => {
      if (fullscreenTimeoutRef.current) {
        clearTimeout(fullscreenTimeoutRef.current);
      }

      chartRootsRef.current.forEach((root) => {
        if (root && typeof root.dispose === 'function') {
          try {
            root.dispose();
          } catch (e) {
            console.error('Error disposing chart root on unmount:', e);
          }
        }
      });

      amCharRefs.current = [null, null, null, null];

      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error(`Error exiting fullscreen on unmount: ${err.message}`);
        });
      }

      // Dispose fullscreen chart root if present
      try {
        if (fullscreenChartRootRef.current && typeof fullscreenChartRootRef.current.dispose === 'function') {
          fullscreenChartRootRef.current.dispose();
        }
      } catch (e) {
        console.error('Error disposing fullscreen chart root on unmount:', e);
      } finally {
        fullscreenChartRootRef.current = null;
        const container = document.getElementById('fullscreen-chart-container');
        if (container) container.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    dispatch(calculateAndSetChartDataCounts(sortedData ?? []));
  }, [sortedData, dispatch]);

  // Callbacks for chart hover events – rely on amCharts cursor sync; keep as no-ops
  const createChartCallbacks = () => ({
    onHover: () => {
      /* noop: rely on amCharts syncWith */
    },
    onMouseLeave: () => {
      /* noop */
    },
  });

  const addAmChartRef = (index: number, chart: XYChart) => {
    if (chart) {
      amCharRefs.current[index] = chart;
      // Register with shared refs for cross-component syncing
      if (onChartRegistered) {
        onChartRegistered(index, chart);
      }
    }
  };

  const syncCursor = syncCursorUtil.bind(null, amCharRefs);

  // Create combined chart refs for syncing (includes both TimeseriesChart and external charts)
  const getCombinedChartRefs = (): (XYChart | null)[] => {
    // Exclude the fullscreen chart from sync recipients while fullscreen is active
    const combinedRefs = [...amCharRefs.current].filter(
      (_, idx) => fullscreenChartIndex === null || idx !== fullscreenChartIndex,
    );
    if (externalChartRefs?.current) {
      combinedRefs.push(...externalChartRefs.current);
    }
    return combinedRefs;
  };

  // Throttle syncAxes to animation frames to avoid event storms on large data ranges
  const syncAxes = (() => {
    let ticking = false;
    return (chart?: XYChart | null) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const combinedRefs = { current: getCombinedChartRefs() };
        syncAxesUtil(combinedRefs, chart);
        // After syncing axes, compute zoom state and update store
        try {
          const charts = combinedRefs.current || [];
          const anyZoomed = charts.some((c: any) => {
            if (!c || !c.xAxes || typeof c.xAxes.getIndex !== 'function') return false;
            const xAxis = c.xAxes.getIndex(0);
            if (!xAxis || typeof xAxis.get !== 'function') return false;
            const start = xAxis.get('start');
            const end = xAxis.get('end');
            return (typeof start === 'number' && start > 0) || (typeof end === 'number' && end < 1);
          });
          dispatch(setChartsZoomState(anyZoomed));
        } catch (e) {
          console.error('Error computing zoom state:', e);
        }
      });
    };
  })();

  // Create charts when chart data changes
  useEffect(() => {
    if (!sortedData || sortedData.length === 0) {
      return;
    }

    // Reset Y-axis info store to avoid unbounded growth across re-renders
    yAxisInfoArray.length = 0;

    // Reset amCharts refs array size to current chart count
    amCharRefs.current = new Array(sortedData.length).fill(null);

    // Clean up previous charts to prevent memory leaks
    chartRootsRef.current.forEach((root) => {
      if (root && typeof root.dispose === 'function') {
        try {
          root.dispose();
        } catch (e) {
          console.error('Error disposing chart:', e);
        }
      }
    });

    // Create new charts
    const newRoots: (Am5Root | null)[] = [];

    // Create charts for each data set
    for (let i = 0; i < sortedData.length; i++) {
      const chartRef = chartRefs.current[i];
      const currentChart = sortedData[i];

      const currentTrendData = parameterData?.find((trend: TrendConfig) => trend.name === currentChart?.originalTitle);

      // Heuristics to reduce heavy features on very large datasets
      const approximateWidth = chartRef?.clientWidth || 800;
      const pointLimit = Math.max(800, Math.min(8000, Math.floor(approximateWidth * 2))); // ~2 pts/px
      const dataCount = Array.isArray(currentChart?.dataPoints) ? (currentChart?.dataPoints?.length ?? 0) : 0;
      const limitExceeded = dataCount > pointLimit;

      const chartConfigValues: ChartConfig = {
        chartColor: currentTrendData?.lineColor || '#00a86b',
        lineType: currentTrendData?.lineType || 'solid',
        lineWeight: currentTrendData?.lineWeight || 2,
        markerColor: currentTrendData?.markerColor || '#2A8C1D',
        markerType: currentTrendData?.markerType || 'circle',
        markerSize: currentTrendData?.markerSize || 4,
      };
      if (chartRef) {
        try {
          const { root } = createTimeSeriesChart(
            chartRef,
            sortedData[i]?.dataPoints || [],
            sortedData[i]?.maxThresholdValues || [],
            sortedData[i]?.minThresholdValues || [],
            createChartCallbacks(),
            chartConfigValues,
            sortedData[i]?.title,
            sortedData[i]?.shortUnitOfMeasure || '-',
            addAmChartRef,
            i,
            syncCursor,
            syncAxes,
            {
              ...chartViewOptions,
              showTooltip: chartViewOptions?.showTooltip && !limitExceeded,
              showSD: chartViewOptions?.showSD && !limitExceeded,
              showEvents: chartViewOptions?.showEvents && !limitExceeded,
            },
            downTimeSliceData,
            pumpCheckerEventsSlice,
            startDate,
            endDate,
          );
          newRoots.push(root);
        } catch (e) {
          console.error(`Error creating chart ${i}:`, e);
          newRoots.push(null);
        }
      } else {
        newRoots.push(null);
      }
    }

    chartRootsRef.current = newRoots;
    // Ensure cursors are synced once after all charts are created
    try {
      syncCursor();
    } catch (e) {
      console.error('Error syncing cursors after creation:', e);
    }

    // Cleanup function
    return () => {
      newRoots.forEach((root) => {
        if (root && typeof root.dispose === 'function') {
          try {
            root.dispose();
          } catch (e) {
            console.error('Error disposing chart in cleanup:', e);
          }
        }
      });
    };
  }, [
    chartData,
    chartViewOptions,
    timeseriesChartData,
    yAxisRefreshVersion,
    downTimeSliceData,
    ...(chartType !== 'rl-well-charts' ? [pumpCheckerEventsSlice] : []),
  ]);

  useEffect(() => {
    if (fullscreenChartIndex !== null && chartData[fullscreenChartIndex]) {
      const chart = sortedData[fullscreenChartIndex];
      const chartRef = document.getElementById('fullscreen-chart-container');
      if (chartRef) {
        // Dispose any existing fullscreen chart before creating a new one
        try {
          if (fullscreenChartRootRef.current && typeof fullscreenChartRootRef.current.dispose === 'function') {
            fullscreenChartRootRef.current.dispose();
          }
        } catch (e) {
          console.error('Error disposing previous fullscreen chart:', e);
        } finally {
          fullscreenChartRootRef.current = null;
          chartRef.innerHTML = '';
        }
        const currentTrendData = parameterData?.find((trend: TrendConfig) => trend.name === chart.originalTitle);

        const approximateWidth = chartRef?.clientWidth || 1200;
        const pointLimit = Math.max(800, Math.min(8000, Math.floor(approximateWidth * 2)));
        const dataCount = Array.isArray(chart?.dataPoints) ? chart.dataPoints.length : 0;
        const limitExceeded = dataCount > pointLimit;

        const chartConfigValues: ChartConfig = {
          chartColor: currentTrendData?.lineColor || '#00a86b',
          lineType: currentTrendData?.lineType || 'solid',
          lineWeight: currentTrendData?.lineWeight || 2,
          markerColor: currentTrendData?.markerColor || '#2A8C1D',
          markerType: currentTrendData?.markerType || 'circle',
          markerSize: currentTrendData?.markerSize || 4,
        };
        try {
          const { root } = createTimeSeriesChart(
            chartRef,
            chart?.dataPoints || [],
            chart?.maxThresholdValues || [],
            chart?.minThresholdValues || [],
            createChartCallbacks(),
            chartConfigValues,
            '',
            chart?.shortUnitOfMeasure || '',
            addAmChartRef,
            fullscreenChartIndex,
            syncCursor,
            syncAxes,
            {
              ...chartViewOptions,
              showTooltip: chartViewOptions?.showTooltip && !limitExceeded,
              showSD: chartViewOptions?.showSD && !limitExceeded,
              showEvents: chartViewOptions?.showEvents && !limitExceeded,
            },
            downTimeSliceData,
            pumpCheckerEventsSlice,
            startDate,
            endDate,
          );
          fullscreenChartRootRef.current = root;
          // Reset zoom in fullscreen: ensure x-axis spans full range
          try {
            const fsChart = amCharRefs.current[fullscreenChartIndex];
            const xAxis = fsChart?.xAxes?.getIndex?.(0);
            if (xAxis) {
              xAxis.set('start', 0);
              xAxis.set('end', 1);
              // Prevent fullscreen chart from broadcasting axis sync events
              (xAxis as unknown as { _skipSync?: boolean })._skipSync = true;
            }
          } catch (e) {
            console.error('Error resetting fullscreen zoom:', e);
          }
          // Sync cursors to include fullscreen chart
          try {
            syncCursor();
          } catch (e) {
            console.error('Error syncing cursors for fullscreen chart:', e);
          }
        } catch (e) {
          console.error('Error creating chart in fullscreen:', e);
        }
      }
    }
    // Cleanup: dispose fullscreen chart when dependencies change
    return () => {
      try {
        if (fullscreenChartRootRef.current && typeof fullscreenChartRootRef.current.dispose === 'function') {
          fullscreenChartRootRef.current.dispose();
        }
      } catch (e) {
        console.error('Error disposing fullscreen chart on cleanup:', e);
      } finally {
        fullscreenChartRootRef.current = null;
        const container = document.getElementById('fullscreen-chart-container');
        if (container) container.innerHTML = '';
      }
    };
  }, [fullscreenChartIndex, chartData, ...(chartType !== 'rl-well-charts' ? [pumpCheckerEventsSlice] : [])]);

  const handleFullscreen = (index: number) => {
    // Cache current chart ref and its zoom state before entering fullscreen
    const currentChart = amCharRefs.current[index] || null;
    preFullscreenChartRef.current = currentChart;
    try {
      const xAxis = currentChart?.xAxes?.getIndex?.(0);
      const start = xAxis?.get?.('start');
      const end = xAxis?.get?.('end');
      preFullscreenZoomRef.current = {
        index,
        start: typeof start === 'number' ? start : undefined,
        end: typeof end === 'number' ? end : undefined,
      };
      xAxis?.set('start', 0);
      xAxis?.set('end', 1);
    } catch (e) {
      // If reading zoom fails, still proceed to fullscreen; we'll just not restore
      preFullscreenZoomRef.current = { index };
    }

    setFullscreenChartIndex(index);

    fullscreenTimeoutRef.current = setTimeout(() => {
      const fullscreenModal = document.querySelector('.fullscreen-modal');
      if (fullscreenModal && !document.fullscreenElement) {
        fullscreenModal.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    }, 0);
  };

  const handleCloseFullscreen = () => {
    setFullscreenChartIndex(null);

    if (fullscreenTimeoutRef.current) {
      clearTimeout(fullscreenTimeoutRef.current);
      fullscreenTimeoutRef.current = null;
    }

    // Exit fullscreen mode if active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }

    // Dispose fullscreen chart resources and clear container
    try {
      if (fullscreenChartRootRef.current && typeof fullscreenChartRootRef.current.dispose === 'function') {
        fullscreenChartRootRef.current.dispose();
      }
    } catch (e) {
      console.error('Error disposing fullscreen chart on close:', e);
    } finally {
      fullscreenChartRootRef.current = null;
      const container = document.getElementById('fullscreen-chart-container');
      if (container) container.innerHTML = '';
    }

    // Restore the pre-fullscreen zoom level on the original chart instance
    try {
      const cached = preFullscreenZoomRef.current;
      const originalChart = preFullscreenChartRef.current;
      if (cached && typeof cached.index === 'number' && originalChart) {
        const xAxis = originalChart?.xAxes?.getIndex?.(0);
        if (xAxis) {
          if (typeof cached.start === 'number') {
            xAxis.set('start', cached.start);
          }
          if (typeof cached.end === 'number') {
            xAxis.set('end', cached.end);
          }
        }
        // Rebind the chart ref back to the original chart for future sync
        amCharRefs.current[cached.index] = originalChart;
      }
    } catch (e) {
      console.error('Error restoring pre-fullscreen zoom:', e);
    } finally {
      preFullscreenZoomRef.current = null;
      preFullscreenChartRef.current = null;
    }
  };

  const handleChartDownload = (option: string, index: number) => {
    const chart = sortedData[index];
    const fileName = `${chart.title}${chart.shortUnitOfMeasure ? ` (${chart.shortUnitOfMeasure})` : ''}_TimeSeriesChart`;
    const header = `${chart.title}${chart.shortUnitOfMeasure ? ` (${chart.shortUnitOfMeasure})` : ''}`;
    handleChartDownloadUtil(option, index, fullscreenChartIndex, fileName, header);
  };

  if (chartDataLoading) {
    return <Loader />;
  }

  type ChartSummary = {
    minValue?: number;
    medianValue?: number;
    maxValue?: number;
    shortUnitOfMeasure?: string;
    date?: string;
    title?: string;
    value?: number | string;
  };
  const ChartStats: React.FC<{ chart: ChartSummary }> = ({ chart }) => (
    <div className='flex'>
      <div>
        <div className='flex justify-center items-center flex-nowrap'>
          <span className='chart-stats__stat'>
            Min: {chart.minValue}
            {chart.shortUnitOfMeasure ? ` ${chart.shortUnitOfMeasure}` : ''}
          </span>
        </div>
      </div>
      <div className='view-controls__divider'></div>
      <div>
        <div className='flex justify-center items-center flex-nowrap'>
          <span className='chart-stats__stat'>
            Mean: {chart.medianValue}
            {chart.shortUnitOfMeasure ? ` ${chart.shortUnitOfMeasure}` : ''}
          </span>
        </div>
      </div>
      <div className='view-controls__divider'></div>
      <div>
        <div className='flex justify-center items-center flex-nowrap'>
          <span className='chart-stats__stat'>
            Max: {chart.maxValue}
            {chart.shortUnitOfMeasure ? ` ${chart.shortUnitOfMeasure}` : ''}
          </span>
        </div>
      </div>
    </div>
  );

  if (!hasPlottableData) return <EmptyState />;

  return (
    <div className='stacked-chart-container'>
      <div className='flex flex-col gap-[8px] items-start self-stretch'>
        {sortedData?.map((chart: ChartItem, index: number) => (
          <section key={chart.id} className='chart-section'>
            {chart.dataPoints && chart.dataPoints.length > 0 ? (
              <div id={`chart-wrapper-${index}`} className='w-full'>
                <div className='flex w-full flex-col justify-center items-center'>
                  <div className='flex w-full flex-col justify-center items-end'>
                    <ChartStats chart={chart} />
                    <div className='w-full h-[181px] flex'>
                      <div ref={(el) => (chartRefs.current[index] = el)} className='chart-container' />
                      <div className='flex w-[175px] flex-col items-end'>
                        <div className='trend-chart-controls'>
                          <ProtectAction canAccess={rPermission.canExpand}>
                            <button
                              analytics-id={`trend-chart-fullscreen-btn-${index}`}
                              className='chart-action-button'
                              onClick={() => handleFullscreen(index)}
                            >
                              <FullScreenIcon />
                            </button>
                          </ProtectAction>
                          <DownloadChart
                            onClick={(option) => handleChartDownload(option, index)}
                            canExportJpg={rPermission.canExportJpg}
                            canExportPdf={rPermission.canExportPdf}
                          />
                        </div>
                        <div className='chart-tooltip'>
                          <span className='chart-tooltip__value'>
                            {chart.value}
                            {chart.shortUnitOfMeasure ? ` ${chart.shortUnitOfMeasure}` : ''}
                          </span>
                          <span className='chart-tooltip__time'>{chart.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='chart-wrapper timeseries-no-data-wrapper'>
                  <NoDataComponent name={chart.title} />
                </div>
              </>
            )}
          </section>
        ))}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenChartIndex !== null && sortedData[fullscreenChartIndex] && (
        <div className='fullscreen-modal'>
          <div className='fullscreen-header'>
            <div className='fullscreen-header-components'>
              <span className='fullcreen-header-content'>{sortedData[fullscreenChartIndex].title}</span>
              <button
                analytics-id='fullscreen-modal-close-btn'
                className='close-button'
                onClick={handleCloseFullscreen}
              >
                <CloseIcon />
              </button>
            </div>
            <div className='min-max flex justify-between items-center gap-[15px]'>
              <ChartStats chart={sortedData[fullscreenChartIndex]} />
              <DownloadChart
                onClick={(option) => handleChartDownload(option, fullscreenChartIndex)}
                canExportJpg={rPermission.canExportJpg}
                canExportPdf={rPermission.canExportPdf}
              />
            </div>
          </div>
          <div id='fullscreen-chart-container' className='fullscreen-chart-container' />
        </div>
      )}
    </div>
  );
};

export default TimeSeriesCharts;
